import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Card, CardContent, Chip, Button } from '@mui/material';
import {
  Code, Brush, TrendingUp, SupportAgent,
  Laptop, Groups, FitnessCenter, School,
  ArrowForward, CheckCircle, LocationOn, Schedule,
} from '@mui/icons-material';
import PublicLayout from '../components/layout/PublicLayout';

const openings = [
  { icon: <Code sx={{ fontSize: 28, color: '#dc2626' }} />, title: 'Senior Backend Engineer', team: 'Engineering', type: 'Full-time', location: 'Remote / India', desc: 'Build and scale our NestJS-based email API platform. Experience with TypeORM, PostgreSQL, MongoDB, and mail protocols required.' },
  { icon: <Brush sx={{ fontSize: 28, color: '#dc2626' }} />, title: 'UI/UX Designer', team: 'Design', type: 'Full-time', location: 'Remote', desc: 'Design beautiful, intuitive interfaces for our webmail, admin panel, and marketing pages. Strong portfolio of web product design required.' },
  { icon: <TrendingUp sx={{ fontSize: 28, color: '#dc2626' }} />, title: 'DevOps / Infrastructure Engineer', team: 'Infrastructure', type: 'Full-time', location: 'Remote / India', desc: 'Manage our Proxmox virtualization cluster, HAProxy/nginx load balancers, PM2 deployments, and automated SSL pipelines.' },
  { icon: <SupportAgent sx={{ fontSize: 28, color: '#dc2626' }} />, title: 'Customer Success Manager', team: 'Support', type: 'Full-time', location: 'Remote', desc: 'Help enterprise customers succeed with CruvzMail. Onboarding, migration support, email deliverability troubleshooting, and ongoing relationship management.' },
];

const perks = [
  { icon: <Laptop sx={{ fontSize: 28, color: '#dc2626' }} />, title: 'Remote-First', desc: 'Work from anywhere. We believe great work happens wherever you\'re most comfortable and effective.' },
  { icon: <FitnessCenter sx={{ fontSize: 28, color: '#dc2626' }} />, title: 'Health & Wellness', desc: 'Comprehensive health insurance, gym allowance, and mental health support for you and your family.' },
  { icon: <School sx={{ fontSize: 28, color: '#dc2626' }} />, title: 'Learning Budget', desc: 'Annual stipend for courses, conferences, books, and professional certifications of your choice.' },
  { icon: <Groups sx={{ fontSize: 28, color: '#dc2626' }} />, title: 'Team Retreats', desc: 'Annual team meetups where we collaborate, celebrate wins, and plan the future of CruvzMail together.' },
];

const whyUs = [
  'High-impact work — your code serves real businesses daily',
  'Small team, big ownership — no bureaucracy, no waiting',
  'Modern tech stack — NestJS, React 19, TypeScript, PostgreSQL',
  'Async-first culture with flexible hours and no micromanagement',
  'Competitive compensation benchmarked to global market rates',
  'Equity participation for early team members',
];

const CareersPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <PublicLayout>

      {/* ── HERO ── */}
      <Box sx={{
        pt: { xs: '120px', md: '145px' }, pb: { xs: 10, md: 14 }, px: { xs: 2, md: 5 },
        background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 50%, #1e0a0a 100%)',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -150, right: -150, width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(220,38,38,0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -100, left: -100, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ maxWidth: 820, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Chip label="We're Hiring" sx={{ mb: 3, bgcolor: 'rgba(220,38,38,0.15)', color: '#fca5a5', fontWeight: 700, fontSize: '0.78rem', border: '1px solid rgba(220,38,38,0.3)', letterSpacing: '0.06em' }} />
          <Typography variant="h1" sx={{
            fontWeight: 900, fontSize: { xs: '2.4rem', md: '3.8rem' }, lineHeight: 1.1, mb: 3,
            background: 'linear-gradient(135deg, #ffffff 30%, #fca5a5 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>
            Build the Future of Business Email
          </Typography>
          <Typography sx={{ fontSize: { xs: '1rem', md: '1.15rem' }, color: 'rgba(255,255,255,0.6)', maxWidth: 620, mx: 'auto', lineHeight: 1.85, mb: 5 }}>
            Join a small, passionate team building the email platform businesses deserve. Remote-first, impact-driven, and obsessed with craft.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => { const el = document.getElementById('open-positions'); el?.scrollIntoView({ behavior: 'smooth' }); }} endIcon={<ArrowForward />} sx={{
              px: 4, py: 1.5, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none',
              bgcolor: '#dc2626', color: '#fff', '&:hover': { bgcolor: '#b91c1c' },
            }}>View Open Roles</Button>
            <Button onClick={() => navigate('/about')} sx={{
              px: 4, py: 1.5, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none',
              color: 'rgba(255,255,255,0.8)', border: '1.5px solid rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.4)' },
            }}>Our Story</Button>
          </Box>
        </Box>
      </Box>

      {/* ── WHY US ── */}
      <Box sx={{ bgcolor: '#0f172a', py: { xs: 6, md: 8 }, px: { xs: 2, md: 5 }, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 6, alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 1.5 }}>Why CruvzMail</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.6rem', md: '2.2rem' }, color: '#fff', lineHeight: 1.2, mb: 4 }}>
              Real Work. Real Impact. Real Ownership.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {whyUs.map((item) => (
                <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <CheckCircle sx={{ fontSize: 18, color: '#4ade80', flexShrink: 0, mt: 0.2 }} />
                  <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 }}>{item}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Grid container spacing={2}>
              {perks.map((p) => (
                <Grid size={{ xs: 12, sm: 6 }} key={p.title}>
                  <Box sx={{
                    p: 3, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
                    transition: 'all 0.2s', '&:hover': { bgcolor: 'rgba(220,38,38,0.08)', borderColor: 'rgba(220,38,38,0.3)' },
                  }}>
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'rgba(220,38,38,0.15)', display: 'inline-flex', mb: 1.5 }}>{p.icon}</Box>
                    <Typography sx={{ fontWeight: 700, color: '#fff', mb: 0.5, fontSize: '0.95rem' }}>{p.title}</Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.65 }}>{p.desc}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Box>

      {/* ── OPEN POSITIONS ── */}
      <Box id="open-positions" sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 1.5 }}>Opportunities</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 1 }}>Open Positions</Typography>
            <Typography sx={{ color: '#64748b', fontSize: '1rem' }}>Join us in building something people depend on every day</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {openings.map((o) => (
              <Card key={o.title} elevation={0} sx={{
                borderRadius: 3, border: '1px solid #e2e8f0',
                transition: 'all 0.25s', '&:hover': { borderColor: '#fecaca', boxShadow: '0 8px 30px rgba(220,38,38,0.07)', transform: 'translateY(-1px)' },
              }}>
                <CardContent sx={{ p: { xs: 3, md: 4 }, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, gap: 3 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fef2f2', display: 'inline-flex', flexShrink: 0 }}>{o.icon}</Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#0f172a', mb: 0.5 }}>{o.title}</Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.65, mb: 1.5 }}>{o.desc}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label={o.team} size="small" sx={{ bgcolor: '#fee2e2', color: '#dc2626', fontSize: '0.7rem', fontWeight: 700 }} />
                      <Chip label={o.type} size="small" sx={{ bgcolor: '#f0fdf4', color: '#16a34a', fontSize: '0.7rem', fontWeight: 700 }} />
                      <Chip
                        icon={<LocationOn sx={{ fontSize: '12px !important', color: '#64748b !important' }} />}
                        label={o.location} size="small"
                        sx={{ bgcolor: '#f8fafc', color: '#64748b', fontSize: '0.7rem', fontWeight: 600 }}
                      />
                      <Chip
                        icon={<Schedule sx={{ fontSize: '12px !important', color: '#64748b !important' }} />}
                        label="Open" size="small"
                        sx={{ bgcolor: '#f8fafc', color: '#64748b', fontSize: '0.7rem', fontWeight: 600 }}
                      />
                    </Box>
                  </Box>
                  <Button onClick={() => navigate('/contact')} endIcon={<ArrowForward />} sx={{
                    px: 3, py: 1.2, borderRadius: '8px', fontSize: '0.85rem', fontWeight: 700, textTransform: 'none',
                    color: '#dc2626', border: '1.5px solid #fecaca', flexShrink: 0,
                    '&:hover': { bgcolor: '#fef2f2', borderColor: '#dc2626' },
                  }}>Apply Now</Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── CTA ── */}
      <Box sx={{
        py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 },
        background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 50%, #1e0a0a 100%)',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -100, left: '50%', transform: 'translateX(-50%)', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(220,38,38,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" sx={{
            fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.6rem' }, mb: 2,
            background: 'linear-gradient(135deg, #ffffff 30%, #fca5a5 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>Don't See Your Role?</Typography>
          <Typography sx={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.6)', mb: 5, maxWidth: 480, mx: 'auto', lineHeight: 1.8 }}>
            We're always open to meeting talented people. Send us your resume and tell us how you'd make CruvzMail better.
          </Typography>
          <Button onClick={() => navigate('/contact')} endIcon={<ArrowForward />} sx={{
            px: 5, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none',
            bgcolor: '#dc2626', color: '#fff', '&:hover': { bgcolor: '#b91c1c' },
          }}>Get in Touch</Button>
        </Box>
      </Box>

    </PublicLayout>
  );
};

export default CareersPage;
